import { useEffect, useState } from "react";
import { Shirt, ShoppingBasket, Smartphone, Sparkles } from "lucide-react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError, storePath } from "../lib/api";
import { forgetSignupPlan, signupIntent } from "../lib/signup";
import { languages, useI18n } from "../lib/i18n";
import { countries, countryName, currencies } from "../lib/countries";
import { useStoreEvents } from "../lib/realtime";
import { TopBar } from "../components/Shell";
import { Banner, Button, Card, cx, Field, fieldError, Input, Select, Skeleton, Spinner } from "../components/ui";

type Created = { store_id: string; handle: string };
type Provisioning = { finished: boolean; steps: { name: string; state: "pending" | "done" | "failed" }[] };

const colors = ["#1c1917", "#9f1239", "#0f766e", "#1d4ed8", "#a16207", "#6d28d9"];
const industryIcons = { fashion: Shirt, electronics: Smartphone, grocery: ShoppingBasket, beauty: Sparkles } as const;

export function CreateStore() {
  const { t, locale } = useI18n();
  const { org } = useSearch({ strict: false }) as { org?: string };
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("QA");
  const [currency, setCurrency] = useState("QAR");
  const [locales, setLocales] = useState<string[]>(["en", "ar"]);
  const [color, setColor] = useState(colors[0]);
  const [industry, setIndustry] = useState("fashion");
  const [seedDemo, setSeedDemo] = useState(true);
  const [created, setCreated] = useState<Created | null>(null);

  const pickCountry = (code: string) => {
    setCountry(code);
    const c = countries.find((x) => x.code === code);
    // The country's languages the platform speaks (languages.json).
    if (c) { setCurrency(c.currency); setLocales(c.locales.filter((l) => languages.some((x) => x.code === l))); }
  };

  const create = useMutation({
    mutationFn: () => api<Created>(org ? `/admin/v1/partners/${org}/development-stores` : "/admin/v1/stores", {
      // The address comes from the name when it's free, else a random one (server side).
      // The plan picked on the website's pricing page starts the trial (merchant stores only).
      body: { name, country, currency, locales, color, industry, seed_demo: seedDemo, plan: org ? undefined : signupIntent().plan || undefined },
    }),
    onSuccess: (c) => { forgetSignupPlan(); setCreated(c); },
    // A plan no longer on sale: the next try starts on the default plan.
    onError: (e) => { if (e instanceof ApiError && e.code === "invalid_plan") forgetSignupPlan(); },
  });

  if (created) return <ProvisioningScreen created={created} name={name} />;

  const total = 3;
  const err = create.error;
  const canNext = step === 1 ? name.trim() !== "" : true;
  return (
    <div className="min-h-full">
      <TopBar />
      <form className="mx-auto max-w-xl space-y-4 px-4 py-8" onSubmit={(e) => {
        e.preventDefault();
        if (step < total) setStep(step + 1); else create.mutate();
      }}>
        <div>
          <p className="text-sm text-stone-500">{t("create.step", { n: step, total })}</p>
          <h1 className="text-xl font-semibold">{t("create.title")}</h1>
        </div>
        {err && <Banner tone="critical">{err.message}</Banner>}
        <Card>
          <div className="space-y-4">
            {step === 1 && (
              <>
                <Field label={t("create.name")} hint={t("create.name_hint")} error={fieldError(err, "name")}>
                  <Input autoFocus value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
                </Field>
              </>
            )}
            {step === 2 && (
              <>
                <Field label={t("create.country")}>
                  <Select value={country} onChange={(e) => pickCountry(e.target.value)}>
                    {countries.map((c) => <option key={c.code} value={c.code}>{countryName(c, locale)}</option>)}
                  </Select>
                </Field>
                <Field label={t("create.currency")} hint={t("create.currency_hint")}>
                  <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>{currencies.map((c) => <option key={c}>{c}</option>)}</Select>
                </Field>
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">{t("create.languages")}</legend>
                  {languages.map(({ code, endonym: label }) => (
                    <label key={code} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={locales.includes(code)}
                        onChange={(e) => setLocales(e.target.checked ? [...locales, code] : locales.length > 1 ? locales.filter((l) => l !== code) : locales)} />
                      {label}
                    </label>
                  ))}
                </fieldset>
              </>
            )}
            {step === 3 && (
              <>
                <Field label={t("sample.industry")}>
                  <div role="radiogroup" aria-label={t("sample.industry")} className="grid grid-cols-2 gap-2 pt-1">
                    {(["fashion", "electronics", "grocery", "beauty"] as const).map((k) => {
                      const Icon = industryIcons[k];
                      return (
                        <button key={k} type="button" role="radio" aria-checked={industry === k} onClick={() => setIndustry(k)}
                          className={cx("flex items-center gap-2 rounded-xl px-3 py-2.5 text-start text-sm ring-1 transition",
                            industry === k ? "bg-brand-tint font-medium ring-brand" : "ring-line hover:bg-muted")}>
                          <Icon className="size-4 shrink-0 text-ink-muted" />{t(`sample.industry.${k}`)}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <label className="flex items-start gap-3 rounded-xl bg-muted p-3 text-sm">
                  <input type="checkbox" className="mt-0.5" checked={seedDemo} onChange={(e) => setSeedDemo(e.target.checked)} />
                  <span>
                    <span className="block font-medium">{t("sample.wizard_title")}</span>
                    <span className="block text-[13px] text-ink-muted">{t("sample.wizard_hint")}</span>
                  </span>
                </label>
                <Field label={t("create.look")} hint={t("create.look_hint")}>
                  <div className="flex flex-wrap gap-3 pt-1">
                    {colors.map((c) => (
                      <button type="button" key={c} aria-label={c} aria-pressed={color === c} onClick={() => setColor(c)}
                        className={cx("size-10 rounded-full ring-offset-2", color === c && "ring-2 ring-brand")} style={{ background: c }} />
                    ))}
                  </div>
                </Field>
              </>
            )}
          </div>
        </Card>
        <div className="flex justify-between">
          {step > 1 ? <Button type="button" onClick={() => setStep(step - 1)}>{t("common.back")}</Button> : <Link to="/stores"><Button type="button">{t("common.cancel")}</Button></Link>}
          <Button variant="primary" type="submit" disabled={!canNext} loading={create.isPending}>{step < total ? t("common.continue") : t("create.submit")}</Button>
        </div>
      </form>
    </div>
  );
}

function ProvisioningScreen({ created, name }: { created: Created; name: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const status = useQuery({
    queryKey: ["provisioning", created.store_id],
    queryFn: () => api<Provisioning>(storePath(created.store_id, "/provisioning")),
    refetchInterval: (q) => (q.state.data?.finished ? false : 3000), // fallback if the stream is unavailable
  });
  useStoreEvents(created.store_id, (e) => { if (e.type.startsWith("provisioning.")) status.refetch(); });
  const done = status.data?.finished;
  useEffect(() => {
    if (done) {
      const id = setTimeout(() => navigate({ to: "/store/$handle", params: { handle: created.handle } }), 1200);
      return () => clearTimeout(id);
    }
  }, [done, navigate, created.handle]);

  const label = (n: string) => (["domain", "subscription", "activate"].includes(n) ? t(`create.step.${n}` as "create.step.domain")
    : n === "sample_data" ? t("sample.wizard_title") : t("create.step.other"));
  return (
    <div className="min-h-full">
      <TopBar />
      <div className="mx-auto max-w-md space-y-4 px-4 py-16 text-center">
        <h1 className="text-xl font-semibold">{done ? t("create.ready") : t("create.provisioning", { name })}</h1>
        {!done && <p className="text-sm text-stone-600">{t("create.provisioning_hint")}</p>}
        <Card>
          <ol className="space-y-2 text-start text-sm">
            {status.data?.steps.map((s) => (
              <li key={s.name} className="flex items-center gap-2">
                {s.state === "done" ? <span className="text-emerald-600">✓</span> : s.state === "failed" ? <Spinner className="size-3.5 text-amber-600" /> : <Spinner className="size-3.5 text-stone-400" />}
                <span className={cx(s.state === "done" && "text-stone-500")}>{label(s.name)}</span>
                {s.state === "failed" && <span className="text-xs text-amber-700">{t("create.failed_step")}</span>}
              </li>
            )) ?? <Skeleton lines={3} />}
          </ol>
        </Card>
        {done && <Link to="/store/$handle" params={{ handle: created.handle }}><Button variant="primary">{t("create.open")}</Button></Link>}
      </div>
    </div>
  );
}
